import { useState } from "react";
import { Save, Loader2, Plus, Trash2, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useContent } from "@/contexts/ContentContext";
import ImageUploader from "@/components/admin/ImageUploader";

const slugify = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const PortfolioEditor = () => {
  const { content, update } = useContent();
  const [draft, setDraft] = useState(content.portfolio);
  const [busy, setBusy] = useState(false);

  const onField = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });

  const updateCard = (i, k, v) => {
    const cards = [...(draft.cards || [])];
    cards[i] = { ...cards[i], [k]: v };
    setDraft({ ...draft, cards });
  };
  const addCard = () => {
    const cards = [...(draft.cards || [])];
    const baseId = `new-game-${cards.length + 1}`;
    cards.push({
      id: baseId,
      title: "New Game",
      tag: "Genre",
      desc: "Short blurb about this upcoming game.",
      eta: "TBD",
      coming_soon: true,
      image_id: null,
    });
    setDraft({ ...draft, cards });
  };
  const removeCard = (i) => setDraft({ ...draft, cards: (draft.cards || []).filter((_, idx) => idx !== i) });
  const move = (i, dir) => {
    const cards = [...(draft.cards || [])];
    const j = i + dir;
    if (j < 0 || j >= cards.length) return;
    [cards[i], cards[j]] = [cards[j], cards[i]];
    setDraft({ ...draft, cards });
  };

  const save = async () => {
    setBusy(true);
    // Ensure all cards have an id
    const cards = (draft.cards || []).map((c, idx) => ({
      ...c,
      id: c.id?.trim() ? slugify(c.id) : slugify(c.title) || `card-${idx + 1}`,
    }));
    // Ensure unique ids
    const seen = new Set();
    cards.forEach((c, idx) => {
      let id = c.id;
      let k = 2;
      while (seen.has(id)) {
        id = `${c.id}-${k++}`;
      }
      cards[idx].id = id;
      seen.add(id);
    });
    const payload = { ...draft, cards };
    try {
      await update("portfolio", payload);
      setDraft(payload);
      toast.success("Portfolio updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="portfolio-editor">
      <div className="space-y-1.5">
        <Label>Section eyebrow</Label>
        <Input data-testid="portfolio-editor-eyebrow" value={draft.eyebrow || ""} onChange={onField("eyebrow")} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Title (line 1)</Label>
          <Input data-testid="portfolio-editor-title1" value={draft.title_line1 || ""} onChange={onField("title_line1")} />
        </div>
        <div className="space-y-1.5">
          <Label>Title (line 2, accent)</Label>
          <Input data-testid="portfolio-editor-title2" value={draft.title_line2 || ""} onChange={onField("title_line2")} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Description</Label>
        <Textarea rows={3} data-testid="portfolio-editor-desc" value={draft.description || ""} onChange={onField("description")} />
      </div>
      <div className="space-y-1.5">
        <Label>Follow CTA label</Label>
        <Input data-testid="portfolio-editor-followcta" value={draft.follow_cta_label || ""} onChange={onField("follow_cta_label")} />
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-base">Cards</Label>
          <Button type="button" variant="outline" size="sm" onClick={addCard} data-testid="portfolio-editor-card-add" className="rounded-lg">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add card
          </Button>
        </div>
        <div className="mt-3 space-y-4">
          {(draft.cards || []).map((card, i) => (
            <div key={i} data-testid={`portfolio-editor-card-${i}`} className="rounded-2xl border border-[hsl(var(--border))] bg-white p-5">
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-1.5">
                  <GripVertical className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Card {i + 1}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button type="button" variant="outline" size="sm" onClick={() => move(i, -1)} disabled={i === 0} className="h-8 px-2 rounded-lg">Up</Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => move(i, 1)} disabled={i === (draft.cards || []).length - 1} className="h-8 px-2 rounded-lg">Down</Button>
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeCard(i)} data-testid={`portfolio-editor-card-${i}-remove`} className="text-[hsl(var(--muted-foreground))] hover:text-[#D94A14] h-8 w-8">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-1">
                  <ImageUploader
                    value={card.image_id}
                    onChange={(id) => updateCard(i, "image_id", id)}
                    label="Card image (optional)"
                    testId={`portfolio-editor-card-${i}-image`}
                  />
                  {!card.image_id && (
                    <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">
                      No image → uses default illustrated SVG (for ids: sleepy-sloth, cosmic-cat, pixel-picnic).
                    </p>
                  )}
                </div>
                <div className="lg:col-span-2 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">ID (slug)</Label>
                      <Input data-testid={`portfolio-editor-card-${i}-id`} value={card.id || ""} onChange={(e) => updateCard(i, "id", e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Title</Label>
                      <Input data-testid={`portfolio-editor-card-${i}-title`} value={card.title || ""} onChange={(e) => updateCard(i, "title", e.target.value)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Tag (genre)</Label>
                      <Input data-testid={`portfolio-editor-card-${i}-tag`} value={card.tag || ""} onChange={(e) => updateCard(i, "tag", e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">ETA</Label>
                      <Input data-testid={`portfolio-editor-card-${i}-eta`} value={card.eta || ""} onChange={(e) => updateCard(i, "eta", e.target.value)} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Description</Label>
                    <Textarea data-testid={`portfolio-editor-card-${i}-desc`} rows={2} value={card.desc || ""} onChange={(e) => updateCard(i, "desc", e.target.value)} />
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      id={`pf-coming-${i}`}
                      checked={card.coming_soon !== false}
                      onCheckedChange={(v) => updateCard(i, "coming_soon", v)}
                      data-testid={`portfolio-editor-card-${i}-comingsoon`}
                    />
                    <Label htmlFor={`pf-coming-${i}`} className="text-sm">Show “Coming Soon” badge</Label>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} data-testid="portfolio-editor-save" className="h-10 rounded-xl bg-[#F35A1F] text-white hover:bg-[#D94A14]">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Portfolio
        </Button>
      </div>
    </div>
  );
};

export default PortfolioEditor;
